import dayjs, { Dayjs, QUnitType } from 'dayjs';
import { last, reduce, first } from 'lodash-es';
import duration from 'dayjs/plugin/duration';
import {
  ACTIVITY_MAX_LAST_DAYS,
  COMMON_TIME_FORMAT_END_D,
  COMMON_TIME_FORMAT_HHmm,
  COMMON_TIME_FORMAT_HHmmss,
} from '../constants';
import { MeetingTime, PeriodsTime } from './../constants/activity';

dayjs.extend(duration);

// 不使用moment确保时区是当前电脑时区
export function getLocalTimezoneOffset() {
  const offset = new Date().getTimezoneOffset() / -60;
  return `UTC${offset >= 0 ? '+' : ''}${offset}`;
}

export function getTimezone(dayjs: Dayjs) {
  const offset = dayjs.utcOffset() / 60;
  return `UTC${offset >= 0 ? '+' : ''}${offset}`;
}

export const formatYYYYMMDD = (dateString: string | null, separator = '/') => {
  if (!dateString) return '';
  return dayjs(dateString).format(`YYYY${separator}MM${separator}DD`);
};

export const formatYYYYMMDDHHmm = (dateString: string | null | number, separator = '/') => {
  if (!dateString) return '';
  return dayjs(dateString).format(`YYYY${separator}MM${separator}DD ${COMMON_TIME_FORMAT_HHmm}`);
};

function formatTime(time: number, spliter = '/') {
  let date = new Date(time);
  let year = date.getFullYear();
  let month = date.getMonth() + 1 >= 10 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1);
  let day = date.getDate() >= 10 ? date.getDate() : '0' + date.getDate();
  return `${year}${spliter}${month}${spliter}${day}`;
}

export const getDateRangeFromStartAndEndTimeStamp = (start: number, end: number) => {
  const res: string[] = [];
  for (let i: number = start; i <= end; ) {
    res.push(formatTime(i, '/'));
    i += 24 * 60 * 60 * 1000;
  }
  return res;
};

export const getFuturalDays = (start: number, duration: number) => {
  const targetDay = start + 1000 * 60 * 60 * 24 * duration;
  return getDateRangeFromStartAndEndTimeStamp(start, targetDay);
};

export const getDateRangeFromStartAndEndMoment = (start: Dayjs | undefined | null, end: Dayjs | undefined | null) => {
  const startDate = start?.format(COMMON_TIME_FORMAT_END_D);
  const endDate = end?.format(COMMON_TIME_FORMAT_END_D);
  const st = startDate?.split('/');
  const et = endDate?.split('/');
  const [stYear, stMonth, stDay] = st ?? [];
  const [etYear, etMonth, etDay] = et ?? [];
  const parsedSt = new Date(Number(stYear), Number(stMonth) - 1, Number(stDay)).getTime();
  const parsedEt = new Date(Number(etYear), Number(etMonth) - 1, Number(etDay)).getTime();

  const count = Math.ceil((parsedEt - parsedSt) / (24 * 60 * 60 * 1000));
  return count >= ACTIVITY_MAX_LAST_DAYS
    ? getFuturalDays(parsedSt, ACTIVITY_MAX_LAST_DAYS - 1)
    : getFuturalDays(parsedSt, count);
};

export const getTupleMeetingTimeList = (meeting_time: MeetingTime[] | null) => {
  if (!(meeting_time || []).length) return [];
  const timeList = reduce(
    meeting_time,
    (acc: string[][], cur: MeetingTime) => {
      const tmp = cur.time.map(item => item.split(','));
      acc.push(...tmp);
      return acc;
    },
    []
  );
  return timeList;
};

/**
 * 在接口返回的已排序的meeting_time中，找到第一个非空时间和最后一个非空时间；
 * 长期活动，每日活动时间可以为0，所以不能只简单取数组第一个和最后一个元素；
 * 正序和倒序各遍历一遍，找到就退出循环；
 * @param meetingTime 接口返回meeting_time字段
 */
export const getMeetingTimeRange = (meetingTime: MeetingTime[] | null) => {
  if (!meetingTime) return [];
  const length = meetingTime.length;
  let startTime = '';
  let endTime = '';
  for (let i = 0; i < length; i++) {
    if (meetingTime[i].time?.length > 0) {
      startTime = meetingTime[i].time[0].split(',')[0];
      break;
    }
  }
  for (let j = length - 1; j >= 0; j--) {
    if (meetingTime[j].time?.length > 0) {
      endTime = last(meetingTime[j].time)!.split(',')[1];
      break;
    }
  }
  return [startTime, endTime];
};

export function judgeCurrentTimeIs30MinutesBeforeStart(startTime: string) {
  if (!startTime) return false;
  return dayjs().diff(dayjs(startTime.split(',')[0]), 'minutes') > -30;
}

export const formatHHmm = (dateString: string | null) => {
  if (!dateString) return;
  return dayjs(dateString).format(COMMON_TIME_FORMAT_HHmm);
};

export function getFormatDuration(counts: number) {
  const time = dayjs.duration(counts, 'seconds');
  const hours = time.hours();
  const minutes = time.minutes();
  const seconds = time.seconds();
  if (hours) {
    return dayjs.duration({ hours: hours, minutes: minutes, seconds: seconds }).format(COMMON_TIME_FORMAT_HHmmss);
  }
  if (minutes) return dayjs.duration({ minutes: minutes, seconds: seconds }).format('mm:ss');
  if (seconds) return dayjs.duration({ seconds: seconds }).format('00:ss');
  return null;
}

export const getCodeValidDisabledDate = (activityEndTime: Dayjs) => (current: Dayjs) => {
  const tooLate = current.clone().startOf('day') > activityEndTime.clone().add(1, 'd');
  return tooLate;
};

export const getDiffTime = (times: Dayjs[], foot: QUnitType = 'hours') => {
  return times[1].diff(times[0], foot);
};

export const getMapDiffTime = (timesList: Dayjs[][], foot: QUnitType = 'hours') => {
  let total = 0;
  timesList.forEach(item => {
    total = total + getDiffTime(item, foot);
  });
  return total;
};

export const formateHours2HHMM = (originalHours?: number) => {
  if (!originalHours) return '-';
  const hours = Math.floor(originalHours);
  const minutes = (originalHours - Math.floor(originalHours)) * 60;

  if (!minutes) {
    return `${hours}小时`;
  }

  return `${hours}小时${minutes}分`;
};

export const formateMinutes2HHMM = (originalMinutes?: number) => {
  if (!originalMinutes) return '-';
  const hours = Math.floor(originalMinutes / 60);
  const minutes = originalMinutes - hours * 60;

  if (!minutes) {
    return `${hours}小时`;
  }

  return `${hours}小时${minutes}分`;
};

// 获取所有时间段中的开始和结束时间
export const getStartEndTime = (sortAllPeriods: PeriodsTime[] | undefined) => {
  if (!sortAllPeriods || sortAllPeriods.length === 0) return [];

  const length = sortAllPeriods.length;
  let startTime: Dayjs = dayjs();
  let endTime: Dayjs = dayjs();

  for (let i = 0; i < length; i++) {
    if (sortAllPeriods[i].time.length > 0) {
      startTime = first(first(sortAllPeriods[i].time))!;
      break;
    }
  }
  for (let j = length - 1; j >= 0; j--) {
    if (sortAllPeriods[j].time.length > 0) {
      endTime = last(last(sortAllPeriods[j].time))!;
      break;
    }
  }
  return [startTime, endTime];
};

export const strArrToMomentArr = (strArr: string[] | null) => {
  if (!(strArr || []).length) return [];
  return strArr!.map(str => str.split(',').map(item => dayjs(item)));
};

export const formatSecondsToHHMM = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds / 60) % 60);
  if (h === 0) {
    return `${m}分钟`;
  } else {
    return `${h}小时${m}分钟`;
  }
};

const UTC8_DICT = ['UTC+08:00', 'UTC+8', '(UTC+08:00)'];

export const convertUTC8 = (offset: string | undefined) => {
  if (offset && UTC8_DICT.includes(offset)) {
    return '(北京时间)';
  }
  return offset?.includes('(') ? offset : `(${offset})`;
};
